import dynamic from 'next/dynamic';
import type { ReactElement } from 'react';
import React, { useEffect, useState } from 'react';

import useCheckInitialRender from '@/components/hooks/useCheckInitialRender';
import { useRegisterDialog } from '@/components/hooks/useRegisterDialog';
import {
  findTabIndexFromModel,
  useScrollByParamsAndHash,
} from '@/components/hooks/useScrollByParamsAndHash';
import CustomMetatag from '@/components/layouts/custom-metatag';
import Layout from '@/components/layouts/lcv';
import ProductHeroBannerXSeries from '@/components/sections/my24/product/hero-banner-x-series';
import { MODEL_YEAR_2024_1_e5, X_SERIES_CLASS } from '@/const/product';
import { DATA } from '@/contents/lcv/product/bluepower/x-series';
import {
  parseMenuData,
  parseModelData,
  sortXseries,
} from '@/contents/lcv/product/utility';
import { getLCVModelByClass } from '@/services/contentstack';

const ProductNavigation = dynamic(
  () => import('@/components/sections/my24/product/navigation'),
  {
    ssr: false,
  }
);
const ProductVideo = dynamic(
  () => import('@/components/sections/my24/product/video'),
  {
    ssr: false,
  }
);
const ProductExteriorInterior = dynamic(
  () => import('@/components/sections/my24/product/exterior-interior-x-series'),
  {
    ssr: false,
  }
);
const ProductCustomize = dynamic(
  () =>
    import('@/components/shared/product-customize').then(
      (mod) => mod.ProductCustomize
    ),
  {
    ssr: false,
  }
);
const ProductSafetyInnovation = dynamic(
  () =>
    import('@/components/sections/my24/product/safety-innovation').then(
      (module) => module.LCVProductInnovationSafety
    ),
  {
    ssr: false,
  }
);
// const BrochureDownload = dynamic(
//   () =>
//     import('@/components/sections/einstein/product/brochure-download').then(
//       (module) => module.BrochureDownload
//     ),
//   {
//     ssr: false,
//   }
// );
const ProductLineup = dynamic(
  () =>
    import('@/components/sections/einstein/product/lineup').then(
      (mod) => mod.LCVProductLineup
    ),
  {
    ssr: false,
  }
);
const FooterLCVGroup = dynamic(
  () => import('@/components/layouts/lcv/footer-group'),
  {
    ssr: false,
  }
);

export async function getStaticProps() {
  const MODEL_RESPONSE = await getLCVModelByClass(
    X_SERIES_CLASS,
    MODEL_YEAR_2024_1_e5
  );
  const MODEL = sortXseries(
    parseModelData(MODEL_RESPONSE?.data?.entries || [])
  );

  return {
    props: JSON.parse(
      JSON.stringify({
        MODEL,
      })
    ),
  };
}

export default function Page({ MODEL }) {
  const MENU = parseMenuData(DATA || {});
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const { api: registerDialog, component: registerDialogComponent } =
    useRegisterDialog({
      theme: 'dark',
      lcvModel: 'pickup',
      model: 'x-series',
      remark: 'blue power',
    });

  const isInitialRender = useCheckInitialRender();
  useEffect(() => {
    if (!isInitialRender || !registerDialog) return;

    registerDialog.addButtonClick(
      'banner',
      DATA?.PRODUCT_HERO_BANNER_DATA?.items?.[0]?.buttons
    );
    registerDialog.addButtonClick(
      'navigation',
      DATA?.PRODUCT_NAVIGATION_DATA.buttons ?? []
    );
  }, [isInitialRender, registerDialog]);

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
  };

  const resetTab = () => {
    setSelectedTab(0); // Reset to the default tab
  };

  const exteriorTabs = DATA?.PRODUCT_EXTERIOR_DATA?.tabs || [];

  useScrollByParamsAndHash(
    56,
    (modelParam: string) => {
      const index = findTabIndexFromModel(modelParam, exteriorTabs);
      setSelectedTab(index);
    },
    resetTab
  );

  return (
    <>
      {!!(
        DATA?.META_TAG_DATA?.title ||
        DATA?.META_TAG_DATA?.description ||
        DATA?.META_TAG_DATA?.img?.url
      ) && (
        <CustomMetatag
          title={DATA?.META_TAG_DATA?.title}
          description={DATA?.META_TAG_DATA?.description}
          imgURL={DATA?.META_TAG_DATA?.img?.url}
        />
      )}
      <main>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <ProductHeroBannerXSeries {...DATA?.PRODUCT_HERO_BANNER_DATA} />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <ProductVideo {...DATA?.PRODUCT_VIDEO_DATA} />
        <ProductNavigation
          {...DATA?.PRODUCT_NAVIGATION_DATA}
          menuModels={MENU}
        />
        <ProductExteriorInterior
          {...DATA?.PRODUCT_EXTERIOR_DATA}
          onTabChange={handleTabChange}
          tabState={selectedTab}
        />
        <ProductExteriorInterior
          {...DATA?.PRODUCT_INTERIOR_DATA}
          onTabChange={handleTabChange}
          tabState={selectedTab}
        />
        <ProductCustomize {...DATA?.PRODUCT_CUSTOMIZE_DATA} listModel={MODEL} />
        <ProductExteriorInterior {...DATA?.PRODUCT_POWER_DATA} />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <ProductSafetyInnovation {...DATA?.PRODUCT_SAFETY_DATA} />
        {/* <BrochureDownload {...DATA?.PRODUCT_BROCHURE_DATA} /> */}
        <ProductLineup {...DATA?.PRODUCT_LINEUP_DATA} />
        {registerDialogComponent}
      </main>
      <FooterLCVGroup />
    </>
  );
}

Page.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
